import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { CheckIcon, ChevronRightIcon, ClockIcon, ExclamationTriangleIcon, EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import { useAuth } from "../../contexts/AuthContext";
import useWorkOrderMutations from "../../data/useWorkOrderMutations";
import {
  ICreateWorkOrderRequest,
  IWorkOrder,
  WorkOrderItemType,
  WorkOrderPriority,
  WorkOrderStatus,
} from "../../models/WorkOrder";
import Modal from "../../layouts/Modal";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import WorkOrderForm from "./WorkOrderForm";
import WorkOrderPartsModal from "./WorkOrderPartsModal";
import WorkOrdersSearchBar from "./WorkOrdersSearchBar";
import Tooltip from "../shared/Tooltip";
import SearchPaletteTypePill from "../shared/Inputs/SearchPaletteTypePill";
import { SearchPaletteItemType } from "../shared/Inputs/SearchPaletteTypes";
import openInNewTab from "../../utils/openInNewTab";
import PagedTable from "../shared/Tables/PagedTable";
import AssigneePill from "./AssigneePill";

const colHelper = createColumnHelper<IWorkOrder>();

export default function WorkOrdersTable() {
  const { claims, userId } = useAuth();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [showCompleted, setShowCompleted] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isPartsOpen, setIsPartsOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<IWorkOrder | null>(null);
  const [workOrderToDelete, setWorkOrderToDelete] = useState<IWorkOrder | null>(null);
  const { create, update, updateStatus, remove } = useWorkOrderMutations();

  const statusOptions = useMemo(
    () =>
      Object.values(WorkOrderStatus)
        .filter((x) => isNaN(Number(x)))
        .map((statusText) => ({
          label: formatEnumLabel(statusText as string),
          value: String(WorkOrderStatus[statusText as keyof typeof WorkOrderStatus]),
        })),
    []
  );

  const priorityOptions = useMemo(
    () =>
      Object.values(WorkOrderPriority)
        .filter((x) => isNaN(Number(x)))
        .map((priorityText) => ({
          label: priorityText as string,
          value: String(WorkOrderPriority[priorityText as keyof typeof WorkOrderPriority]),
        })),
    []
  );

  const additionalBody = useMemo(() => {
    const body: Record<string, any> = {};

    if (status !== "all") body.status = Number(status) as WorkOrderStatus;
    if (priority !== "all") body.priority = Number(priority) as WorkOrderPriority;

    if (claims?.isAdmin && assignee !== "all") {
      if (assignee === "unassigned") {
        body.assigneeIsNull = { fieldName: "AssigneeId" };
      } else {
        body.assigneeId = assignee;
      }
    }

    if (showCompleted) body.includeCompleted = true;

    return body;
  }, [status, priority, claims?.isAdmin, assignee, showCompleted]);

  const columns = useMemo(
    () => [
      colHelper.display({
        id: "expand",
        header: "",
        enableSorting: false,
        cell: (info) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              info.row.toggleExpanded();
            }}
            style={{ transform: `rotate(${!info.row.getIsExpanded() ? "0" : "-270"}deg)` }}
            className="p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        ),
      }),
      colHelper.accessor((row) => row.title, {
        id: "OrderNumber",
        header: "Order Number",
        sortingFn: "auto",
        cell: (info) => <span>{info.row.original.orderNumber}</span>,
      }),
      colHelper.accessor((row) => row.title, {
        id: "Title",
        header: "Title",
        sortingFn: "auto",
        cell: (info) => <span>{info.row.original.title}</span>,
      }),
      colHelper.accessor((row) => row.notes, {
        id: "Notes",
        header: "Notes",
        sortingFn: "auto",
        cell: (info) => <span>{info.row.original.notes}</span>,
      }),
      colHelper.accessor((row) => row.assigneeId, {
        id: "AssigneeId",
        header: "Assignee",
        sortingFn: "auto",
        cell: (info) => <AssigneePill assigneeId={info.row.original.assigneeId} />,
      }),
      colHelper.display({
        id: "items",
        header: "Items",
        enableSorting: false,
        cell: (info) => <span className="font-medium">{info.row.original.workOrderItems?.length ?? 0}</span>,
      }),
      colHelper.accessor((row) => row.priority, {
        id: "Priority",
        header: "Priority",
        sortingFn: "auto",
        cell: (info) => (
          <StatusPill
            text={getPriorityLabel(info.row.original.priority)}
            colour={getPriorityColour(info.row.original.priority)}
            icon={info.row.original.priority === WorkOrderPriority.Critical ? ExclamationTriangleIcon : undefined}
          />
        ),
      }),
      colHelper.accessor((row) => row.status, {
        id: "Status",
        header: "Status",
        sortingFn: "auto",
        cell: (info) => (
          <div onClick={(e) => e.stopPropagation()}>
            <select
              value={info.row.original.status}
              onChange={(e) =>
                updateStatus.mutate({
                  workOrderId: info.row.original.id,
                  status: { status: Number(e.target.value) as WorkOrderStatus },
                })
              }
              className={getStatusSelectClassName(info.row.original.status)}
            >
              {Object.values(WorkOrderStatus)
                .filter((x) => isNaN(Number(x)))
                .map((value) => {
                  const enumValue = WorkOrderStatus[value as keyof typeof WorkOrderStatus];
                  return (
                    <option key={value as string} value={enumValue}>
                      {getStatusLabel(enumValue)}
                    </option>
                  );
                })}
            </select>
          </div>
        ),
      }),
      colHelper.accessor((row) => row.dueDate, {
        id: "DueDate",
        header: "Due Date",
        sortingFn: "auto",
        cell: (info) => <DueDateCell dateValue={info.row.original.dueDate} status={info.row.original.status} />,
      }),
      colHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: (info) => (
          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            {info.row.original.status !== WorkOrderStatus.Complete && (
              <Tooltip content="Mark as complete">
                <CheckIcon
                  onClick={() => {
                    updateStatus.mutate({
                      workOrderId: info.row.original.id,
                      status: { status: WorkOrderStatus.Complete },
                    });
                  }}
                  className="w-5 h-5 text-green-600 hover:cursor-pointer"
                />
              </Tooltip>
            )}

            <Tooltip content="View parts">
              <EyeIcon
                onClick={() => {
                  setSelectedWorkOrder(info.row.original);
                  setIsPartsOpen(true);
                }}
                className="w-5 h-5 text-gray-600 hover:cursor-pointer"
              />
            </Tooltip>

            <Tooltip content="Edit work order">
              <PencilIcon
                onClick={() => {
                  setSelectedWorkOrder(info.row.original);
                  setIsEditOpen(true);
                }}
                className="w-5 h-5 text-gray-600 hover:cursor-pointer"
              />
            </Tooltip>
            <Tooltip content="Delete work order">
              <TrashIcon
                onClick={() => {
                  setWorkOrderToDelete(info.row.original);
                  setIsDeleteOpen(true);
                }}
                className="w-5 h-5 text-red-600 hover:cursor-pointer"
              />
            </Tooltip>
          </div>
        ),
      }),
    ],
    [updateStatus]
  );

  return (
    <>
      <WorkOrdersSearchBar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        priority={priority}
        onPriorityChange={setPriority}
        showAssigneeFilter={Boolean(claims?.isAdmin)}
        assignee={assignee}
        onAssigneeChange={setAssignee}
        showCompleted={showCompleted}
        onShowCompletedChange={setShowCompleted}
        statusOptions={statusOptions}
        priorityOptions={priorityOptions}
        onNewOrderClick={() => setIsCreateOpen(true)}
      />

      <Modal open={isCreateOpen} setOpen={setIsCreateOpen} width="max-w-5xl">
        <WorkOrderForm
          createdById={userId ?? null}
          onCancel={() => setIsCreateOpen(false)}
          isSubmitting={create.isLoading}
          onSubmit={(request: ICreateWorkOrderRequest) => create.mutate(request)}
          onComplete={() => setIsCreateOpen(false)}
          initialLinkedItems={[]}
        />
      </Modal>

      <Modal open={isEditOpen} setOpen={setIsEditOpen} width="max-w-5xl">
        <WorkOrderForm
          workOrder={selectedWorkOrder}
          createdById={userId ?? null}
          onCancel={() => setIsEditOpen(false)}
          isSubmitting={update.isLoading}
          onSubmit={(request: ICreateWorkOrderRequest) => {
            if (!selectedWorkOrder?.id) return;
            update.mutate({ workOrderId: selectedWorkOrder.id, request });
          }}
          onComplete={() => setIsEditOpen(false)}
          initialLinkedItems={[]}
        />
      </Modal>

      <Modal open={isDeleteOpen} setOpen={setIsDeleteOpen} width="max-w-lg">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Delete work order?</h2>
          <p className="text-sm text-gray-600">
            This action cannot be undone. Are you sure you want to delete{" "}
            <span className="font-medium text-gray-900">
              {workOrderToDelete ? `"${workOrderToDelete.title}"` : "this work order"}
            </span>
            ?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                if (remove.isLoading) return;
                setIsDeleteOpen(false);
                setWorkOrderToDelete(null);
              }}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={remove.isLoading || !workOrderToDelete}
              onClick={() => {
                if (!workOrderToDelete) return;
                remove.mutate(workOrderToDelete.id, {
                  onSuccess: () => {
                    setIsDeleteOpen(false);
                    setWorkOrderToDelete(null);
                  },
                });
              }}
              className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {remove.isLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      <WorkOrderPartsModal
        workOrder={selectedWorkOrder}
        open={isPartsOpen}
        setOpen={setIsPartsOpen}
      />

      <PagedTable<IWorkOrder>
        key="work-orders-table"
        url="/workorder/paginated"
        columns={columns}
        queryLabel={undefined}
        externalQuery={search}
        showTotal={true}
        additionalBody={additionalBody}
        onRowClick={(workOrder) => {
          setSelectedWorkOrder(workOrder);
          setIsPartsOpen(true);
        }}
        expandedRow={(workOrder) =>
          workOrder.workOrderItems?.length ? (
            <div className="px-4 py-4 text-sm bg-gray-50 flex flex-wrap gap-2">
              {workOrder.workOrderItems.map((item) => (
                <span
                  key={item.id}
                  className="group inline-flex h-9 max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 pl-1.5 pr-4 text-sm shadow-sm transition-colors hover:border-gray-300 text-gray-800 ring-gray-200 cursor-pointer"
                  onClick={() => openInNewTab(getItemLink(item))}
                >
                  <SearchPaletteTypePill
                    type={mapWorkOrderItemTypeToSearchPaletteItemType(item.type)}
                    nested
                    className="shrink-0"
                  />
                  <span className="min-w-0 max-w-[240px] truncate font-medium text-gray-900">{item.title}</span>
                </span>
              ))}
            </div>
          ) : (
            <div className="px-4 py-4 border text-sm bg-gray-50 text-gray-500">No linked items.</div>
          )
        }
      />
    </>
  );
}

function getItemLink(item: { type: WorkOrderItemType; linkedId: string }): string {
  switch (item.type) {
    case WorkOrderItemType.Order:
      return `/stock/${item.linkedId}`;
    case WorkOrderItemType.Vehicle:
      return `/vehicles/${item.linkedId}`;
    case WorkOrderItemType.Stock:
      return `/stock/${item.linkedId}`;
    case WorkOrderItemType.Lead:
      return `/leads/${item.linkedId}`;
    default:
      return `/vehicles/${item.linkedId}`;
  }
}

function getPriorityLabel(priority: WorkOrderPriority): string {
  switch (priority) {
    case WorkOrderPriority.Low:
      return "Low";
    case WorkOrderPriority.Medium:
      return "Medium";
    case WorkOrderPriority.High:
      return "High";
    case WorkOrderPriority.Critical:
      return "Critical";
    default:
      return "Unknown";
  }
}

function getStatusLabel(status: WorkOrderStatus): string {
  switch (status) {
    case WorkOrderStatus.ToDo:
      return "To Do";
    case WorkOrderStatus.InProgress:
      return "In Progress";
    case WorkOrderStatus.Complete:
      return "Complete";
    default:
      return "Unknown";
  }
}

function getStatusSelectClassName(status: WorkOrderStatus): string {
  const baseClass =
    "w-40 cursor-pointer rounded-full border px-3 py-1 text-sm font-medium focus:outline-none focus:ring-2";

  switch (status) {
    case WorkOrderStatus.ToDo:
      return `${baseClass} border-gray-300 bg-gray-100 text-gray-700 focus:ring-gray-300`;
    case WorkOrderStatus.InProgress:
      return `${baseClass} border-blue-300 bg-blue-100 text-blue-700 focus:ring-blue-300`;
    case WorkOrderStatus.Complete:
      return `${baseClass} border-green-300 bg-green-100 text-green-700 focus:ring-green-300`;
    default:
      return `${baseClass} border-gray-300 bg-gray-100 text-gray-700 focus:ring-gray-300`;
  }
}

function getPriorityColour(priority: WorkOrderPriority): StatusPillColour {
  switch (priority) {
    case WorkOrderPriority.Low:
      return StatusPillColour.Green;
    case WorkOrderPriority.Medium:
      return StatusPillColour.Yellow;
    case WorkOrderPriority.High:
      return StatusPillColour.Orange;
    case WorkOrderPriority.Critical:
      return StatusPillColour.Red;
    default:
      return StatusPillColour.Neutral;
  }
}

function DueDateCell({
  dateValue,
  status,
}: {
  dateValue: string | null;
  status: WorkOrderStatus;
}) {
  if (!dateValue) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  const dueDate = new Date(dateValue);
  if (isNaN(dueDate.getTime())) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  const now = new Date();
  const diffMs = dueDate.getTime() - now.getTime();

  const isComplete = status === WorkOrderStatus.Complete;
  const isOverdue = !isComplete && diffMs < 0;
  const isDueSoon = !isComplete && diffMs <= 1000 * 60 * 60 * 24;

  const textClass = isComplete
    ? "text-green-600"
    : isOverdue
    ? "text-red-600"
    : isDueSoon
    ? "text-amber-700"
    : "text-gray-900";

  return (
    <div className="flex flex-col gap-1">
      <span className={`text-sm font-medium ${textClass}`}>
        {formatDateTime(dueDate)}
      </span>

      {!isComplete && (
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${
              isOverdue
                ? "bg-red-50 text-red-700 ring-red-200"
                : isDueSoon
                ? "bg-amber-50 text-amber-700 ring-amber-200"
                : "bg-gray-50 text-gray-600 ring-gray-200"
            }`}
          >
            <ClockIcon className="h-3.5 w-3.5" />
            {formatRelativeDue(diffMs)}
          </span>

          {isOverdue && (
            <span className="text-xs font-semibold uppercase tracking-wide text-red-600">
              Overdue
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatRelativeDue(diffMs: number): string {
  const absMs = Math.abs(diffMs);

  const totalMinutes = Math.floor(absMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  let value = "";

  if (days > 0) {
    value = `${days}d${hours > 0 ? ` ${hours}h` : ""}`;
  } else if (hours > 0) {
    value = `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`;
  } else {
    value = `${Math.max(minutes, 1)}m`;
  }

  return diffMs < 0 ? `${value} overdue` : `Due in ${value}`;
}

function formatDate(dateValue: string | null): string {
  if (!dateValue) return "-";

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB");
}

function formatEnumLabel(value: string): string {
  return value.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function mapWorkOrderItemTypeToSearchPaletteItemType(type: WorkOrderItemType): SearchPaletteItemType {
  switch (type) {
    case WorkOrderItemType.Order:
      return "Order";
    case WorkOrderItemType.Vehicle:
      return "Vehicle";
    case WorkOrderItemType.Stock:
      return "Stock";
    case WorkOrderItemType.Lead:
      return "Lead";
    default:
      return "Vehicle";
  }
}
